import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import type {
    MediaDocType,
    MediaLinkDocType,
    MediaOwnerType
} from '@ast-data/shared';
import { useStableQuery } from '../../db/useStableQuery';
import { mediaShownBy, type MediaPlace, type ShownMedia } from './mediaLinks';

/** A live query's documents as plain objects, held steady by value. */
export function useDocs<T>(
    collection: string,
    selector: Record<string, unknown>
) {
    const query = useStableQuery<T>({ selector } as never);
    const { results } = useLiveRxQuery<T>({ collection, query });
    return useMemo(
        () => results.map((doc) => doc.toMutableJSON() as T),
        [results]
    );
}

export interface OwnerMedia {
    shown: ShownMedia[];
    /**
     * Every place each shown picture appears — its origin first, then its links
     * — so a card can name where else it is.
     */
    placesByMedia: Map<string, MediaPlace[]>;
    /** Every link of the shown pictures, wherever it points. */
    links: MediaLinkDocType[];
}

/**
 * The photos and sketches one description shows: its own, and those linked
 * into it from another description of the family.
 */
export function useOwnerMedia(
    ownerType: MediaOwnerType,
    ownerId: string
): OwnerMedia {
    const ownMedia = useDocs<MediaDocType>('media', { ownerType, ownerId });
    const linksHere = useDocs<MediaLinkDocType>('media_links', {
        ownerType,
        ownerId
    });
    const linkedMedia = useDocs<MediaDocType>('media', {
        id: { $in: linksHere.map((link) => link.mediaId) }
    });
    const shown = useMemo(
        () => mediaShownBy(ownMedia, linksHere, linkedMedia),
        [ownMedia, linksHere, linkedMedia]
    );
    const links = useDocs<MediaLinkDocType>('media_links', {
        mediaId: { $in: shown.map((item) => item.media.id) }
    });

    const placesByMedia = useMemo(() => {
        const places = new Map<string, MediaPlace[]>();
        for (const { media } of shown) {
            places.set(media.id, [
                { ownerType: media.ownerType, ownerId: media.ownerId }
            ]);
        }
        for (const link of [...links].sort((firstLink, secondLink) =>
            firstLink.createdAt.localeCompare(secondLink.createdAt)
        )) {
            places.get(link.mediaId)?.push({
                ownerType: link.ownerType,
                ownerId: link.ownerId
            });
        }
        return places;
    }, [shown, links]);

    return { shown, placesByMedia, links };
}
